import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { type UseQueryResult, useInfiniteQuery, useQuery } from 'react-query'
import { useNavigate, useParams } from 'react-router-dom'

import avatarPlaceholder from 'assets/images/user_placeholder.png'
import BellLink from 'components/UI/BellLink/BellLink'
import { usePageTitle } from 'context/pageTitleContext'
import CreatorsList from 'features/CreatorsList/CreatorsList'
import LayoutHeader from 'features/LayoutHeader/LayoutHeader'
import WithHeaderSection from 'layouts/WithHeaderSection/WithHeaderSection'
import { getModelFriends } from 'services/endpoints/friendship'
import { getPerformer } from 'services/endpoints/profile'
import { useUserProfile } from 'store'
import { type IProfile } from 'types/interfaces/IProfile'

import styles from './PublicFriends.module.scss'

export const PublicFriends = () => {
    const { setTitle } = usePageTitle()
    const { modelId } = useParams<{ modelId: string | undefined }>()
    const [searchTerm, setSearchTerm] = useState('')
    const [searchSubmit, setSearchSubmit] = useState('')
    const [sort, setSort] = useState<'asc' | 'desc'>('asc')
    const [friends, setFriends] = useState<any>([])
    const [friendsNumber, setFriendsNumber] = useState<number>()
    const navigate = useNavigate()
    const perPage = 20
    const userData = useUserProfile()

    const { data: modelData }: UseQueryResult<IProfile, Error> = useQuery<
        IProfile,
        Error
    >(['profile', modelId], async () => await getPerformer(modelId), {
        enabled: !!modelId,
        staleTime: 15 * 1000
    })

    if (modelData?.public_friends === false)
        navigate('/404', { replace: true })

    const { hasNextPage, isFetching, fetchNextPage, isLoading } = useInfiniteQuery(
    ['friends', modelId],
    async ({ pageParam = 1 }) => {
        if (!modelId) {
        throw new Error('Missing model id')
        }

        return await getModelFriends(modelId, {
        search: '',
        sort: 'asc',
        page: pageParam,
        perPage
        })
    },
    {
        enabled: !!modelId,
        getNextPageParam: lastPage => {
        const nextPage = lastPage?.meta?.current_page + 1
        return nextPage <= lastPage.meta.last_page
            ? nextPage
            : undefined
        },
        onSuccess: resp => {
        // unchanged
        }
    }
    )

    const observer = useRef<IntersectionObserver>()

    const lastCreatorRef = useCallback(
        (node: any) => {
            if (isFetching) return
            if (observer.current) observer.current.disconnect()

            observer.current = new IntersectionObserver(entries => {
                if (entries[0].isIntersecting && hasNextPage) {
                    fetchNextPage()
                }
            })

            if (node) observer.current.observe(node)
        },
        [fetchNextPage, hasNextPage, isFetching]
    )

    const titleContent = useMemo(() => (
    <>
        <img
        className={`footer__link--avatar ${styles.header_avatar}`}
        src={
            modelData?.cropped_avatar.url ??
            modelData?.avatar.url ??
            avatarPlaceholder
        }
        alt='Profile'
        />
        <span className={styles.header_text}>{`${
        modelData?.display_name ?? 'Model'
        }'s Friends`}</span>
    </>
    ), [modelData])


    useEffect(() => {
    setTitle(titleContent)
    }, [setTitle, titleContent])

    return (
        <div className='basic_layout creators__content__wrapper'>
            <WithHeaderSection
                headerSection={
                    <LayoutHeader
                        type='search-with-buttons'
                        searchValue={searchTerm}
                        searchFn={(term: string) => {
                            setSearchTerm(term)
                        }}
                        clearFn={() => {
                            setSearchTerm('')
                        }}
                        searchSettings={{
                            isForm: true,
                            formSubmitFn: e => {
                                e.preventDefault()
                                setSearchSubmit(searchTerm)
                            },
                            iconClickFn: () => {
                                setSearchSubmit(searchTerm)
                            }
                        }}
                        additionalProps={{
                            placeholder: 'Search Friends'
                        }}
                        buttons={[]}
                    />
                }
            >
                <>
                {isLoading && <div className="loader"></div>}

                {!isLoading && (
                    <>
                    {userData?.id === modelData?.id && (
                        <div className={styles.list__header}>
                            <div className={styles.friends__number}>
                                <span>{friendsNumber}</span> Friends
                            </div>
                        </div>
                    )}
                        <CreatorsList
                            creators={friends.filter((friend: any) =>
                                friend.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                                friend.handle?.toLowerCase().includes(searchTerm.toLowerCase())
                            )}
                            customClass="browse-creators-list"
                            renderLocation="publicFriends"
                            creatorRef={lastCreatorRef}
                            customPlaceholderText="Friends appear here"
                        />
                    </>
                )}
                </>
            </WithHeaderSection>
        </div>
    )
}
